import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStoreFeature,
  type,
  withComputed,
  withMethods,
} from '@ngrx/signals';
import { DataLayerTypeState } from './data-layer-type.state';
import { StateFilter } from '../crud.state';
import { DataLayerType } from '../../models/data-layer-type';
import { SafeHtml } from '@angular/platform-browser';
import { IconService } from '../../services/icon/icon.service';
import { catchError, map, Observable, of } from 'rxjs';
export interface DataLayerTypeIcon {
  key: string;
  svg: SafeHtml | undefined;
  color: string;
}
export const filterItems = (items: DataLayerType[], filter: StateFilter) => {
  return items
    .filter(item => {
      const titleMatch = item.name
        .toLowerCase()
        .includes(filter.query.toLowerCase());
      const tagsMatch =
        !filter.tags ||
        filter.tags.length === 0 ||
        (item.tags &&
          filter.tags.some(tagId => item.tags && item.tags.includes(tagId)));

      return titleMatch && tagsMatch;
    })
    .sort((a, b) => a.sorting - b.sorting);
};

export function withDataLayerTypeSelectors() {
  return signalStoreFeature(
    {
      state: type<DataLayerTypeState>(),
    },
    withComputed(({ items, filter }) => ({
      count: computed(() => Object.keys(items()).length),
      asArray: computed(() =>
        Object.values(items()).sort((a, b) => a.sorting - b.sorting)
      ),
      typeSorted: computed(() =>
        Object.values(items()).sort((a, b) =>
          a.type === b.type
            ? a.sorting - b.sorting
            : a.type === 'Polygon'
              ? -1
              : 1
        )
      ),
      polygonLayers: computed(() =>
        Object.values(items())
          .filter(type => type.type === 'Polygon')
          .sort((a, b) => a.sorting - b.sorting)
      ),
      pointLayers: computed(() =>
        Object.values(items())
          .filter(type => type.type === 'Point')
          .sort((a, b) => a.sorting - b.sorting)
      ),
      filteredItems: computed(() => {
        let types = Object.values(items());
        if (filter()) {
          types = filterItems(types, filter());
        }
        return types as DataLayerType[];
      }),
      groupedByType: computed(() => {
        const grouped = filterItems(Object.values(items()), filter()).reduce(
          (acc, dataLayerType) => {
            const type = dataLayerType.type;
            if (!acc[type]) acc[type] = [];
            acc[type].push(dataLayerType);
            return acc;
          },
          {} as Record<'Point' | 'Polygon', DataLayerType[]>
        );

        // Sort each group
        Object.values(grouped).forEach(group =>
          group.sort((a, b) => a.sorting - b.sorting)
        );

        return grouped;
      }),
    })),
    withMethods(
      (
        { items, filter, groupedByType },
        iconService = inject(IconService)
      ) => ({
        getByType(type: 'Point' | 'Polygon') {
          return groupedByType()[type];
        },
        globalSearch(
          query: string,
          tags: string[],
          type?: 'Polygon' | 'Point'
        ): DataLayerType[] {
          let itemsArray = Object.values(items());
          if (type) {
            itemsArray = itemsArray.filter(item => item.type === type);
          }
          const tempFilter: StateFilter = { ...filter(), query, tags };
          return filterItems(itemsArray, tempFilter);
        },
        getPoiIcon(id: string): Observable<DataLayerTypeIcon | null> | null {
          const item = items()[id];
          if (item && item.icon) {
            const svgCodeObservable = iconService.getSvgCodeByKey(item.icon);
            if (svgCodeObservable) {
              return svgCodeObservable.pipe(
                map(svgCode => ({
                  key: item.icon || '',
                  svg: svgCode,
                  color: item.color || '#000000',
                })),
                catchError(() => of(null)) // Return null on error
              );
            }
            return of(null);
          }
          return of(null);
        },
        isPointType(id: string): boolean {
          return items()[id]?.type === 'Point' || false;
        },
      })
    )
  );
}
